import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dossiersAPI, configAPI, gestionnairesAPI, suiviAPI, tableauConfigAPI, statutsProgressionAPI } from '../services/api';
import { 
  Search, Download, Archive, Trash2, RotateCcw, 
  AlertTriangle, MoreVertical, Eye, X, MessageSquare, Filter, ChevronDown, Clock, Accessibility
} from 'lucide-react';

const DossiersList = () => {
  const location = useLocation();
  const { statutCode } = useParams(); // Récupérer le code statut depuis l'URL
  const { user, isSuperAdmin } = useAuth();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statut, setStatut] = useState('');
  const [statuts, setStatuts] = useState([]);
  const [columns, setColumns] = useState([]);
  const [viewMode, setViewMode] = useState('all'); // all, archives, trash
  const [actionMenu, setActionMenu] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [sortBy, setSortBy] = useState(''); // Vide = pas de tri
  const [gestionnaires, setGestionnaires] = useState([]);
  const [remarquesModal, setRemarquesModal] = useState(null); // {dossier, remarques}
  const [loadingRemarques, setLoadingRemarques] = useState(false);
  const [currentStatutLabel, setCurrentStatutLabel] = useState(''); // Label du statut pour l'affichage
  
  // État pour le filtre rapide
  const [filtreRapide, setFiltreRapide] = useState(null); // { champ: 'caisse', valeur: 'AG2R La Mondiale', label: 'Caisse' }
  
  // Colonnes filtrables (données récurrentes)
  const colonnesFiltrables = ['caisse', 'statut', 'installateur', 'amo', 'statut_travaux', 'anah_statut', 'gestionnaire'];

  useEffect(() => {
    loadStatuts();
    loadColumnsConfig();
    loadGestionnaires();
    loadFiltresConfig();
  }, []);

  // Vérifier si un rafraîchissement est nécessaire (après consultation d'un dossier)
  useEffect(() => {
    const needsRefresh = sessionStorage.getItem('dossiers_list_needs_refresh');
    if (needsRefresh === 'true') {
      sessionStorage.removeItem('dossiers_list_needs_refresh');
      loadDossiers();
    }
  });

  useEffect(() => {
    // Déterminer le mode d'affichage depuis l'URL
    if (location.pathname.includes('/corbeille')) {
      setViewMode('trash');
      setStatut('');
      setCurrentStatutLabel('Corbeille');
    } else if (statutCode) {
      // Route dynamique /dossiers/statut/:statutCode
      setViewMode('all');
      setStatut(statutCode);
      // Le label sera mis à jour quand les statuts seront chargés
      const statutInfo = statuts.find(s => s.code === statutCode);
      setCurrentStatutLabel(statutInfo?.label || statutCode);
    } else {
      setViewMode('all');
      setStatut('');
      setCurrentStatutLabel('');
    }
  }, [location, statutCode, statuts]);

  useEffect(() => {
    loadDossiers();
  }, [statut, viewMode, filtreRapide]);

  const loadStatuts = async () => {
    try {
      const res = await statutsProgressionAPI.getAll();
      setStatuts(res.data || []);
    } catch (error) {
      console.error('Erreur chargement statuts:', error);
    }
  };

  const loadColumnsConfig = async () => {
    try {
      // Utiliser la nouvelle configuration depuis tableauConfigAPI
      const res = await tableauConfigAPI.getColonnesConfig();
      const colonnesData = res.data.colonnes || [];
      const visibleCols = colonnesData
        .filter(c => c.visible)
        .sort((a, b) => a.ordre - b.ordre)
        .map(c => ({ code: c.id, label: c.label, key: c.key, width: 'auto' }));
      setColumns(visibleCols);
    } catch (error) {
      console.error('Erreur chargement config colonnes:', error);
      // Config par défaut
      setColumns([
        { code: 'reference', label: 'Référence' },
        { code: 'nom_client', label: 'Nom' },
        { code: 'prenom_client', label: 'Prénom' },
        { code: 'telephone', label: 'Téléphone' },
        { code: 'caisse', label: 'Caisse' },
        { code: 'statut', label: 'Statut' },
        { code: 'date_modification', label: 'Dernière modification' },
        { code: 'gestionnaire', label: 'Gestionnaire' },
      ]);
    }
  };

  const loadGestionnaires = async () => {
    try {
      const res = await gestionnairesAPI.getAll();
      setGestionnaires(res.data || []);
    } catch (error) {
      console.error('Erreur chargement gestionnaires:', error);
    }
  };

  // Simplifié - on n'a plus besoin des filtres personnalisés
  const loadFiltresConfig = async () => {
    // Plus utilisé - les filtres rapides sont appliqués directement
  };

  const handleAssignGestionnaire = async (dossierId, gestionnaireId) => {
    try {
      const gestionnaire = gestionnaires.find(g => g.id === gestionnaireId);
      await dossiersAPI.update(dossierId, { 
        gestionnaire_id: gestionnaireId || null,
        gestionnaire_nom: gestionnaire?.nom || null
      });
      // Rafraîchir la liste
      loadDossiers();
    } catch (error) {
      console.error('Erreur assignation gestionnaire:', error);
    }
  };

  const loadDossiers = async () => {
    try {
      setLoading(true);
      const params = {};
      
      // Gérer les modes archives/corbeille
      if (viewMode === 'archives') {
        params.archived = true;
        params.deleted = false;
      } else if (viewMode === 'trash') {
        params.deleted = true;
      } else {
        params.archived = false;
        params.deleted = false;
        if (statut) params.statut = statut;
      }
      
      if (search) params.search = search;
      
      const response = await dossiersAPI.getAll(params);
      let dossiersData = response.data;
      
      // Appliquer le filtre rapide si actif
      if (filtreRapide) {
        dossiersData = dossiersData.filter(dossier => {
          const valeur = getValeurForFiltre(dossier, filtreRapide.champ);
          return valeur === filtreRapide.valeur;
        });
      }
      
      setDossiers(dossiersData);
    } catch (error) {
      console.error('Erreur chargement dossiers:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Obtenir la valeur d'un dossier pour un champ donné
  const getValeurForFiltre = (dossier, champ) => {
    switch (champ) {
      case 'caisse': return dossier.caisse;
      case 'statut': return dossier.statut;
      case 'installateur': return dossier.nom_installateur;
      case 'amo': return dossier.amoData?.nom;
      case 'statut_travaux': return dossier.installateurData?.statut_travaux;
      case 'anah_statut': return dossier.amoData?.anah_statut;
      case 'gestionnaire': return dossier.gestionnaire_id;
      default: return null;
    }
  };
  
  // Appliquer un filtre rapide
  const appliquerFiltreRapide = (champ, valeur, label) => {
    if (!valeur || valeur === '—') return;
    setFiltreRapide({ champ, valeur, label });
  };
  
  // Enlever le filtre rapide
  const enleverFiltreRapide = () => {
    setFiltreRapide(null);
  };
  
  // Helper pour obtenir une valeur imbriquée
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Fonction de tri des dossiers
  const sortedDossiers = sortBy ? [...dossiers].sort((a, b) => {
    switch (sortBy) {
      case 'date_desc':
        return new Date(b.date_creation) - new Date(a.date_creation);
      case 'date_asc':
        return new Date(a.date_creation) - new Date(b.date_creation);
      case 'prenom_asc':
        return (a.identite?.prenom || '').localeCompare(b.identite?.prenom || '');
      case 'prenom_desc':
        return (b.identite?.prenom || '').localeCompare(a.identite?.prenom || '');
      case 'nom_asc':
        return (a.identite?.nom || '').localeCompare(b.identite?.nom || '');
      case 'nom_desc':
        return (b.identite?.nom || '').localeCompare(a.identite?.nom || '');
      default:
        return 0;
    }
  }) : dossiers; // Si pas de tri, garder l'ordre original

  const handleSearch = (e) => {
    e.preventDefault();
    loadDossiers();
  };

  // Charger les remarques d'un dossier
  const handleShowRemarques = async (dossier) => {
    setLoadingRemarques(true);
    setActionMenu(null);
    try {
      const res = await suiviAPI.getCommentaires(dossier.id);
      setRemarquesModal({
        dossier,
        remarques: res.data || []
      });
    } catch (error) {
      console.error('Erreur chargement remarques:', error);
      setRemarquesModal({
        dossier,
        remarques: []
      });
    } finally {
      setLoadingRemarques(false);
    }
  };

  // Actions sur les dossiers
  const handleArchive = async (dossier) => {
    try {
      await dossiersAPI.archive(dossier.id);
      loadDossiers();
      setActionMenu(null);
    } catch (error) {
      console.error('Erreur archivage:', error);
      alert('Erreur lors de l\'archivage');
    }
  };

  const handleUnarchive = async (dossier) => {
    try {
      await dossiersAPI.unarchive(dossier.id);
      loadDossiers();
      setActionMenu(null);
    } catch (error) {
      console.error('Erreur désarchivage:', error);
      alert('Erreur lors du désarchivage');
    }
  };

  const handleSoftDelete = async (dossier) => {
    setConfirmDialog({
      type: 'delete',
      dossier,
      title: 'Supprimer le dossier',
      message: `Êtes-vous sûr de vouloir supprimer le dossier ${dossier.reference} ? Il sera déplacé dans la corbeille pendant 30 jours.`,
      onConfirm: async () => {
        try {
          await dossiersAPI.softDelete(dossier.id);
          loadDossiers();
          setConfirmDialog(null);
          setActionMenu(null);
        } catch (error) {
          console.error('Erreur suppression:', error);
          alert('Erreur lors de la suppression');
        }
      }
    });
  };

  const handleRestore = async (dossier) => {
    try {
      await dossiersAPI.restore(dossier.id);
      loadDossiers();
      setActionMenu(null);
    } catch (error) {
      console.error('Erreur restauration:', error);
      alert('Erreur lors de la restauration');
    }
  };

  const handlePermanentDelete = async (dossier) => {
    setConfirmDialog({
      type: 'permanent',
      dossier,
      title: 'Suppression définitive',
      message: `Attention ! Cette action est irréversible. Le dossier ${dossier.reference} et toutes ses données seront définitivement supprimés.`,
      onConfirm: async () => {
        try {
          await dossiersAPI.permanentDelete(dossier.id);
          loadDossiers();
          setConfirmDialog(null);
          setActionMenu(null);
        } catch (error) {
          console.error('Erreur suppression définitive:', error);
          alert('Erreur lors de la suppression définitive');
        }
      }
    });
  };

  const getStatusBadge = (st) => {
    const statutConfig = statuts.find(s => s.code === st);
    if (statutConfig) {
      // Convertir la classe Tailwind en couleur
      const colorMap = {
        'bg-blue-500': '#3b82f6',
        'bg-violet-500': '#8b5cf6',
        'bg-orange-500': '#f97316',
        'bg-cyan-500': '#06b6d4',
        'bg-yellow-500': '#eab308',
        'bg-gray-500': '#6b7280',
        'bg-red-500': '#ef4444',
        'bg-green-500': '#22c55e',
        'bg-emerald-600': '#059669',
        'bg-purple-500': '#a855f7',
      };
      const color = colorMap[statutConfig.couleur] || '#6b7280';
      return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium whitespace-nowrap">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }}></span>
          {statutConfig.label}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium whitespace-nowrap text-gray-500">
        <span className="w-2 h-2 rounded-full bg-gray-400"></span>
        {st || 'Inconnu'}
      </span>
    );
  };

  const getPriorityIcon = (priorite) => {
    const icons = { high: '🔴', med: '🟠', low: '🟢' };
    return icons[priorite] || icons.med;
  };

  const getTitle = () => {
    if (viewMode === 'trash') return 'Corbeille';
    if (currentStatutLabel) {
      return currentStatutLabel;
    }
    return 'Tous les dossiers';
  };

  const getSubtitle = () => {
    if (viewMode === 'trash') return 'Les dossiers sont supprimés définitivement après 30 jours';
    return null;
  };

  // Composant pour une cellule filtrable (avec bouton au survol)
  const CelluleFiltrable = ({ dossierId, colCode, valeur, label, children }) => {
    const isFiltered = filtreRapide?.champ === colCode && filtreRapide?.valeur === valeur;
    
    if (!valeur || valeur === '—') {
      return <span className="text-gray-400">—</span>;
    }
    
    return (
      <div className="relative group flex items-center gap-1">
        <span className={isFiltered ? 'font-semibold text-indigo-700' : ''}>{children}</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (isFiltered) {
              enleverFiltreRapide();
            } else {
              appliquerFiltreRapide(colCode, valeur, label);
            }
          }}
          data-testid={`filter-btn-${colCode}-${dossierId}`}
          className={`p-1 rounded transition-all ${
            isFiltered 
              ? 'bg-indigo-100 text-indigo-600 opacity-100' 
              : 'bg-gray-100 text-gray-500 hover:bg-indigo-100 hover:text-indigo-600 opacity-0 group-hover:opacity-100'
          }`}
          title={isFiltered ? 'Enlever ce filtre' : `Filtrer par ${label}: ${valeur}`}
        >
          <Filter className="w-3 h-3" />
        </button>
      </div>
    );
  };

  // Rendu d'une cellule selon la colonne
  const renderCell = (dossier, colCode) => {
    switch (colCode) {
      case 'reference':
        return (
          <Link 
            to={`/dossiers/${dossier.id}`}
            className="font-medium text-green-600 hover:underline whitespace-nowrap"
          >
            {dossier.reference}
          </Link>
        );
      case 'prenom':
      case 'prenom_client':
        return <span className="text-gray-900">{dossier.identite?.prenom}</span>;
      case 'nom':
      case 'nom_client':
        return (
          <span className="flex items-center gap-1.5">
            <span className="text-gray-900">{dossier.identite?.nom}</span>
            {dossier.is_handicap && (
              <span 
                className="text-purple-500" 
                title={dossier.taux_handicap ? `Handicap ${dossier.taux_handicap}%` : 'Personne en situation de handicap'}
              >
                <Accessibility className="w-4 h-4" />
              </span>
            )}
          </span>
        );
      case 'telephone':
        return <span className="text-gray-600">{dossier.identite?.telephone}</span>;
      case 'email':
        return <span className="text-gray-600">{dossier.identite?.email || '—'}</span>;
      case 'caisse':
        return (
          <CelluleFiltrable dossierId={dossier.id} colCode="caisse" valeur={dossier.caisse} label="Caisse">
            <span className="text-gray-600">{dossier.caisse}</span>
          </CelluleFiltrable>
        );
      case 'statut':
        return (
          <CelluleFiltrable dossierId={dossier.id} colCode="statut" valeur={dossier.statut} label="Statut">
            {getStatusBadge(dossier.statut)}
          </CelluleFiltrable>
        );
      case 'priorite':
        return <span className="text-lg">{getPriorityIcon(dossier.priorite)}</span>;
      case 'gestionnaire':
        // Seul l'admin peut changer le gestionnaire
        if (isSuperAdmin) {
          return (
            <select
              value={dossier.gestionnaire_id || ''}
              onChange={(e) => handleAssignGestionnaire(dossier.id, e.target.value)}
              className="px-2 py-1 text-sm border border-gray-200 rounded bg-white hover:border-green-400 focus:ring-2 focus:ring-green-500 cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="">— Non assigné</option>
              {gestionnaires.map(g => (
                <option key={g.id} value={g.id}>{g.nom}</option>
              ))}
            </select>
          );
        } else {
          // Gestionnaire voit juste le nom en lecture seule
          const gestNom = gestionnaires.find(g => g.id === dossier.gestionnaire_id)?.nom;
          return <span className="text-gray-600">{gestNom || '— Non assigné'}</span>;
        }
      case 'date_creation':
        return <span className="text-gray-600 whitespace-nowrap">{new Date(dossier.date_creation).toLocaleDateString('fr-FR')}</span>;
      case 'date_modification':
        const dateMod = dossier.date_modification || dossier.updated_at;
        if (!dateMod) return <span className="text-gray-400">—</span>;
        return (
          <span className="text-gray-600 whitespace-nowrap flex items-center gap-1">
            <Clock className="w-3 h-3 text-gray-400" />
            {new Date(dateMod).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
            {' '}
            <span className="text-gray-400 text-xs">
              {new Date(dateMod).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </span>
        );
      case 'remarques':
        // Afficher un aperçu des remarques avec un bouton pour voir plus
        const nbRemarques = dossier.nb_remarques || 0;
        return (
          <button
            onClick={(e) => { e.stopPropagation(); handleShowRemarques(dossier); }}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition ${
              nbRemarques > 0 
                ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' 
                : 'text-gray-400 hover:bg-gray-100'
            }`}
            title={nbRemarques > 0 ? `${nbRemarques} remarque(s)` : 'Aucune remarque'}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {nbRemarques > 0 && <span>{nbRemarques}</span>}
          </button>
        );
      case 'installateur':
        return (
          <CelluleFiltrable dossierId={dossier.id} colCode="installateur" valeur={dossier.nom_installateur} label="Installateur">
            <span className="text-gray-600">{dossier.nom_installateur || '—'}</span>
          </CelluleFiltrable>
        );
      case 'statut_travaux':
        const statutsTravaux = {
          'non_demarre': { label: 'Non démarrés', color: 'bg-gray-100 text-gray-600' },
          'en_cours': { label: 'En cours', color: 'bg-amber-100 text-amber-700' },
          'termines': { label: 'Terminés', color: 'bg-green-100 text-green-700' },
        };
        const stTravaux = dossier.installateurData?.statut_travaux;
        if (!stTravaux) return <span className="text-gray-400">—</span>;
        const config = statutsTravaux[stTravaux] || { label: stTravaux, color: 'bg-gray-100 text-gray-600' };
        return (
          <CelluleFiltrable dossierId={dossier.id} colCode="statut_travaux" valeur={stTravaux} label="Statut travaux">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>
          </CelluleFiltrable>
        );
      case 'montant_devis':
        const montant = dossier.installateurData?.montant_devis;
        if (!montant) return <span className="text-gray-400">—</span>;
        return <span className="text-gray-600">{montant.toLocaleString('fr-FR')} €</span>;
      case 'amo':
        return (
          <CelluleFiltrable dossierId={dossier.id} colCode="amo" valeur={dossier.amoData?.nom} label="AMO">
            <span className="text-gray-600">{dossier.amoData?.nom || '—'}</span>
          </CelluleFiltrable>
        );
      case 'anah_statut':
        const statutsAnah = {
          'valide': { label: 'Validé', color: 'bg-green-100 text-green-700' },
          'refuse': { label: 'Refusé', color: 'bg-red-100 text-red-700' },
          'en_attente': { label: 'En attente', color: 'bg-amber-100 text-amber-700' },
        };
        const stAnah = dossier.amoData?.anah_statut;
        if (!stAnah) return <span className="text-gray-400">—</span>;
        const configAnah = statutsAnah[stAnah] || { label: stAnah, color: 'bg-gray-100 text-gray-600' };
        return (
          <CelluleFiltrable dossierId={dossier.id} colCode="anah_statut" valeur={stAnah} label="Statut ANAH">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${configAnah.color}`}>{configAnah.label}</span>
          </CelluleFiltrable>
        );
      case 'motif':
        return <span className="text-gray-600">{dossier.motif}</span>;
      case 'ville':
        return <span className="text-gray-600">{dossier.identite?.ville}</span>;
      case 'code_postal':
        return <span className="text-sm text-gray-600">{dossier.identite?.code_postal}</span>;
      case 'nss':
        return <span className="text-sm text-gray-600">{dossier.identite?.nss || '—'}</span>;
      case 'source':
        return <span className="text-sm text-gray-600">{dossier.source}</span>;
      default:
        return '—';
    }
  };

  // Calculer jours restants avant suppression
  const getDaysRemaining = (deletedAt) => {
    if (!deletedAt) return 30;
    const deleted = new Date(deletedAt);
    const now = new Date();
    const diff = 30 - Math.floor((now - deleted) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{getTitle()}</h1>
          {getSubtitle() && (
            <p className="text-sm text-gray-500 mt-1">{getSubtitle()}</p>
          )}
        </div>
        {viewMode === 'all' && (
          <Link 
            to="/nouveau-dossier"
            className="bg-white border border-gray-200 text-gray-600 px-6 py-3 rounded-lg font-semibold hover:bg-green-600 hover:text-white hover:border-transparent transition"
          >
            + Nouvelle demande
          </Link>
        )}
      </div>

      {/* Alerte corbeille */}
      {viewMode === 'trash' && dossiers.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <p className="text-amber-800 text-sm">
            Les dossiers dans la corbeille seront supprimés définitivement après 30 jours.
          </p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Barre de recherche et filtres */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex gap-3 items-center flex-wrap">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2 min-w-[300px]">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  data-testid="search-input"
                />
              </div>
              <button 
                type="submit"
                className="px-4 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                data-testid="search-button"
              >
                Rechercher
              </button>
            </form>

            {/* Filtre statut - seulement dans "Tous" */}
            {viewMode === 'all' && (
              <select
                value={statut}
                onChange={(e) => setStatut(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                data-testid="filter-statut-select"
              >
                <option value="">Tous statuts</option>
                {statuts.filter(s => s.actif).map(s => (
                  <option key={s.code} value={s.code}>
                    {s.icone} {s.label}
                  </option>
                ))}
              </select>
            )}
            
            {/* Tri discret */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-500 bg-gray-50 hover:bg-white focus:ring-2 focus:ring-green-500"
              data-testid="sort-select"
            >
              <option value="">Trier par...</option>
              <option value="date_desc">Plus récent</option>
              <option value="date_asc">Plus ancien</option>
              <option value="prenom_asc">Prénom A-Z</option>
              <option value="prenom_desc">Prénom Z-A</option>
              <option value="nom_asc">Nom A-Z</option>
              <option value="nom_desc">Nom Z-A</option>
            </select>
          </div>
        </div>

        {/* Barre de filtre rapide actif */}
        {filtreRapide && (
          <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-indigo-600" />
              <span className="text-sm text-indigo-700">
                Filtre actif : <strong>{filtreRapide.label}</strong> = <strong>{filtreRapide.valeur}</strong>
              </span>
              <span className="text-xs text-indigo-500">
                ({dossiers.length} résultat{dossiers.length > 1 ? 's' : ''})
              </span>
            </div>
            <button
              onClick={enleverFiltreRapide}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-100 transition text-sm font-medium"
              data-testid="clear-filter-button"
            >
              <X className="w-4 h-4" />
              Enlever le filtre
            </button>
          </div>
        )}

        {/* Tableau */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-r from-pink-500 via-rose-500 to-violet-500">
              <tr>
                {columns.map(col => (
                  <th 
                    key={col.code} 
                    className={`text-left py-3 px-4 font-semibold text-white ${col.code === 'priorite' ? 'text-center' : ''}`}
                    style={{ width: col.width !== 'auto' ? col.width : undefined }}
                  >
                    {col.label}
                  </th>
                ))}
                <th className="text-center py-3 px-4 font-semibold text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedDossiers.map((dossier) => {
                const isNewUnviewed = dossier.statut === 'new' && !dossier.is_viewed;
                return (
                  <tr 
                    key={dossier.id} 
                    className={`border-b border-gray-100 transition ${
                      isNewUnviewed 
                        ? 'bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500' 
                        : 'hover:bg-gray-100'
                    }`}
                    data-testid={`dossier-row-${dossier.id}`}
                  >
                    {columns.map(col => (
                      <td 
                        key={col.code} 
                        className={`py-3 px-4 ${col.code === 'priorite' ? 'text-center' : ''} ${isNewUnviewed ? 'font-semibold text-red-800' : ''}`}
                      >
                        {renderCell(dossier, col.code)}
                      </td>
                    ))}
                    <td className="py-3 px-4 text-center relative">
                      <div className="flex items-center justify-center gap-2">
                        {/* Indicateur nouveau non vu */}
                        {isNewUnviewed && (
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                        )}
                        
                        {/* Voir le dossier */}
                        <Link
                          to={`/dossiers/${dossier.id}`}
                          className={`p-2 rounded-lg transition ${isNewUnviewed ? 'text-red-600 hover:bg-red-100' : 'text-green-600 hover:bg-green-50'}`}
                          title="Voir le dossier"
                          data-testid={`view-dossier-${dossier.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Link>

                      {/* Menu d'actions */}
                      <button
                        onClick={() => setActionMenu(actionMenu === dossier.id ? null : dossier.id)}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition"
                        data-testid={`action-menu-${dossier.id}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>

                      {/* Dropdown menu */}
                      {actionMenu === dossier.id && (
                        <div className="absolute right-6 top-12 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[180px]">
                          {/* Bouton Remarques - toujours visible */}
                          <button
                            onClick={() => handleShowRemarques(dossier)}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 flex items-center gap-2"
                            data-testid={`remarques-dossier-${dossier.id}`}
                          >
                            <MessageSquare className="w-4 h-4 text-blue-600" />
                            <span className="text-blue-600 font-medium">Remarques</span>
                          </button>
                          
                          <div className="border-t border-gray-100 my-1"></div>
                          
                          {viewMode === 'all' && (
                            <>
                              <button
                                onClick={() => handleArchive(dossier)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                data-testid={`archive-dossier-${dossier.id}`}
                              >
                                <Archive className="w-4 h-4" />
                                Archiver
                              </button>
                              <button
                                onClick={() => handleSoftDelete(dossier)}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                data-testid={`delete-dossier-${dossier.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                                Supprimer
                              </button>
                            </>
                          )}

                          {viewMode === 'archives' && (
                            <>
                              <button
                                onClick={() => handleUnarchive(dossier)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                data-testid={`unarchive-dossier-${dossier.id}`}
                              >
                                <RotateCcw className="w-4 h-4" />
                                Désarchiver
                              </button>
                              <button
                                onClick={() => handleSoftDelete(dossier)}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                data-testid={`delete-dossier-${dossier.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                                Supprimer
                              </button>
                            </>
                          )}

                          {viewMode === 'trash' && (
                            <>
                              <button
                                onClick={() => handleRestore(dossier)}
                                className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
                                data-testid={`restore-dossier-${dossier.id}`}
                              >
                                <RotateCcw className="w-4 h-4" />
                                Restaurer
                              </button>
                              <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
                                Suppression dans {getDaysRemaining(dossier.deleted_at)} jours
                              </div>
                              <button
                                onClick={() => handlePermanentDelete(dossier)}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                data-testid={`permanent-delete-dossier-${dossier.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                                Supprimer définitivement
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>

          {dossiers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">
                {viewMode === 'archives' && 'Aucun dossier archivé'}
                {viewMode === 'trash' && 'La corbeille est vide'}
                {viewMode === 'all' && 'Aucun dossier trouvé'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Dialog de confirmation */}
      {confirmDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-3 rounded-full ${confirmDialog.type === 'permanent' ? 'bg-red-100' : 'bg-amber-100'}`}>
                {confirmDialog.type === 'permanent' ? (
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                ) : (
                  <Trash2 className="w-6 h-6 text-amber-600" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{confirmDialog.title}</h3>
            </div>
            <p className="text-gray-600 mb-6">{confirmDialog.message}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmDialog(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition"
                data-testid="cancel-confirm-dialog"
              >
                Annuler
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className={`px-4 py-2 text-white rounded-lg font-medium transition ${
                  confirmDialog.type === 'permanent' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-amber-600 hover:bg-amber-700'
                }`}
                data-testid="confirm-delete-button"
              >
                {confirmDialog.type === 'permanent' ? 'Supprimer définitivement' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay pour fermer le menu */}
      {actionMenu && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setActionMenu(null)}
        />
      )}

      {/* Modal Remarques */}
      {remarquesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-xl">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                  Remarques
                </h3>
                <p className="text-sm text-gray-600">
                  {remarquesModal.dossier.identite?.prenom} {remarquesModal.dossier.identite?.nom} - {remarquesModal.dossier.reference}
                </p>
              </div>
              <button
                onClick={() => setRemarquesModal(null)}
                className="p-2 text-gray-500 hover:bg-white/50 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Contenu */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingRemarques ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : remarquesModal.remarques.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Aucune remarque pour ce dossier</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {remarquesModal.remarques.slice(0, 10).map((remarque, index) => (
                    <div 
                      key={remarque.id || index}
                      className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-700">
                          {remarque.user_nom || 'Utilisateur'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {remarque.date_creation ? new Date(remarque.date_creation).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : ''}
                        </span>
                      </div>
                      <p className="text-sm text-gray-800">{remarque.message}</p>
                      {remarque.categorie && (
                        <span className={`inline-block mt-2 px-2 py-0.5 text-xs rounded-full ${
                          remarque.categorie === 'action' ? 'bg-green-100 text-green-700' :
                          remarque.categorie === 'important' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {remarque.categorie === 'action' ? 'Action faite' : 
                           remarque.categorie === 'important' ? 'Important' : 
                           remarque.categorie}
                        </span>
                      )}
                    </div>
                  ))}
                  {remarquesModal.remarques.length > 10 && (
                    <p className="text-center text-sm text-gray-500 py-2">
                      ... et {remarquesModal.remarques.length - 10} autres remarques
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex gap-3 justify-between">
                <Link
                  to={`/dossiers/${remarquesModal.dossier.id}`}
                  className="px-4 py-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg font-medium transition flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Voir le dossier
                </Link>
                <button
                  onClick={() => setRemarquesModal(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DossiersList;
